import React from "react";
import { inject, observer } from "mobx-react";
import { ApplePayButton } from "./ApplePayButton";
import {
  canUseApplePay,
  getApplePayVersionError,
  isThirdPartyApplePay,
  requestTopLevelApplePayAvailability,
} from "../modules/express-checkout/wallets/apple-pay";
import {
  getMode,
  normalizeExpressCheckoutConfig,
  postExpressCheckoutEvent,
  validateExpressCheckoutConfig,
} from "../modules/express-checkout";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { sanitizeCSS } from "../utils/sanitizeCss";
import { loaderVersionHeaders } from "../utils/loaderVersion";
import { GooglePayButton } from "./GooglePayButton";
import { GooglePay } from "../modules/express-checkout/wallets/google-pay";
import {
  fetchWalletAvailability,
  NO_WALLET_AVAILABILITY,
  resolveAllowedWallets,
} from "../modules/express-checkout/availability";

// Shown for any init/load failure. Payers must never see HTTP status text or
// backend detail (the real error goes to the console only). The component's own
// validation messages (e.g. "Entry point not found") are set separately.
const INIT_ERROR_MESSAGE = "An error occurred while initializing the component.";

const ExpressCheckout = (props = {}) => {
  const [domain, setDomain] = useState();
  const [paypoint, setPaypoint] = useState();
  const [config] = useState(() => {
    const raw = JSON.parse(window.atob(props.match.params.config));
    const normalizedEC = normalizeExpressCheckoutConfig(raw.expressCheckout || {});
    return { ...raw, expressCheckout: normalizedEC };
  });
  const [errorMessage, setErrorMessage] = useState(() => {
    try {
      validateExpressCheckoutConfig(config.expressCheckout);
      return undefined;
    } catch (err) {
      return err.message;
    }
  });
  const [allowedMethods, setAllowedMethods] = useState({
    applePay: false,
    googlePay: false,
  });
  const [applePayMerchantId, setApplePayMerchantId] = useState();

  const resizeContainer = useCallback(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const cr = entry.contentRect;
        window.parent.postMessage(
          {
            event: "callback-payabli-function-resize" + config.randomId,
            data: cr.height,
          },
          "*"
        );
      }
    });
    resizeObserver.observe(document.body);
  }, [config]);

  // Loads the Apple Pay JS SDK. `1.latest` is required for third-party browser
  // support (Chrome/Edge): the SDK polyfills `window.ApplePaySession` and
  // provides the <apple-pay-button> custom element on non-Safari platforms.
  // Must resolve before any availability check so ApplePaySession exists.
  const applePayScript = useCallback(
    () =>
      new Promise((resolve) => {
        const SRC = "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js";
        const existing = document.getElementById("apple-pay-sdk");
        if (existing) {
          if (window.ApplePaySession) {
            resolve();
          } else {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", () => resolve(), { once: true });
          }
          return;
        }
        const script = document.createElement("script");
        script.id = "apple-pay-sdk";
        script.src = SRC;
        script.crossOrigin = "anonymous";
        script.type = "text/javascript";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
          // Resolve (don't reject) so a CDN hiccup degrades gracefully — the
          // availability check below simply hides the Apple Pay button.
          resolve();
        };
        document.head.appendChild(script);
      }),
    []
  );

  const setTokenUI = useCallback(
    (token) => {
      window.parent.postMessage(
        {
          event: "callback-payabli-function-set-token" + config.randomId,
          data: token,
        },
        "*"
      );
    },
    [config]
  );

  const [googlePayInstance, setGooglePayInstance] = useState();
  const loadExpressCheckout = useCallback(
    async ({ initData, availability }, config, domain) => {
      return new Promise(async (resolve, reject) => {
        try {
          if (!initData || !domain) {
            throw new Error("Token or domain data not found");
          }
          // Only `DbaName` is read from here (Google Pay payment request).
          const paypointInfo = { DbaName: initData.dbaName ?? "" };
          setPaypoint(paypointInfo);
          const enabledWallets = resolveAllowedWallets(initData, availability);
          const allowedMethods = {};

          if (config?.expressCheckout?.applePay?.enabled) {
            // Record whether Apple Pay was natively available BEFORE loading the
            // SDK. Safari exposes `window.ApplePaySession` natively; third-party
            // browsers only get it (polyfilled) once the SDK loads. This lets us
            // route Safari to the in-iframe sheet and third-party browsers to the
            // top-level host so the QR escapes the iframe (see isThirdPartyApplePay).
            if (window.__payabliHadNativeApplePaySession === undefined) {
              window.__payabliHadNativeApplePaySession = !!window.ApplePaySession;
            }
            // Load the SDK first so `window.ApplePaySession` exists in Safari
            // AND third-party browsers before we check availability.
            await applePayScript();
            const merchantId = availability?.applePay?.merchantId || undefined;
            const canUse = await canUseApplePay(config, merchantId);
            if (canUse) {
              // In third-party browsers (Chrome/Edge/Firefox) the sheet runs on
              // the merchant's TOP-LEVEL page. Confirm the host can actually run
              // it there — SDK not blocked by the merchant's CSP, and a
              // bridge-capable component.js — before mounting the button, so it
              // never appears only to fail on click. Safari runs in-iframe and
              // needs no probe.
              let hostReady = true;
              if (isThirdPartyApplePay()) {
                hostReady = await requestTopLevelApplePayAvailability(
                  config.randomId
                );
              }
              if (hostReady && enabledWallets.applePay) {
                setApplePayMerchantId(merchantId);
                allowedMethods.applePay = true;
              }
            } else {
              const versionError = getApplePayVersionError(config);
              if (versionError) {
                postExpressCheckoutEvent({
                  kind: "error",
                  randomId: config.randomId,
                  data: {
                    error: versionError,
                    paymentMethod: "apple_pay",
                    flow: getMode(config),
                  },
                });
              }
            }
          }

          if (config?.expressCheckout?.googlePay?.enabled) {
            if (enabledWallets.googlePay) {
              const googlePay = new GooglePay({
                config,
                paypoint: paypointInfo,
                domain,
              });
              const res = await googlePay.loadScript();
              if (res.ok) {
                allowedMethods.googlePay = true;
                setGooglePayInstance(googlePay);
              }
            }
          }

          // Upload custom css
          if (config.customCssUrl) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.type = "text/css";
            link.href = config.customCssUrl;
            document.head.appendChild(link);
          }

          if (
            config.customCssStyle &&
            typeof config.customCssStyle === "string"
          ) {
            const css = sanitizeCSS(config.customCssStyle);
            const style = document.createElement("style");
            style.type = "text/css";
            style.appendChild(document.createTextNode(css));
            document.head.appendChild(style);
          }

          // Resize container
          resizeContainer();

          // Update vTerminal. The token is NOT set here: it was set right after
          // init and has since been rotated by availability (single-use tokens).
          props.vTerminal.setEntryPoint(initData.entryPoint);

          // Set token UI (host page only records it; it is never used for calls)
          setTokenUI(initData.identifier);
          return resolve(allowedMethods);
        } catch (error) {
          console.error(error);
          setErrorMessage(INIT_ERROR_MESSAGE);
          return reject(error);
        }
      }).then((allowedMethods) => {
        // Send ready event
        window.parent.postMessage(
          {
            event:
              "callback-payabli-function-mounted-component" + config.randomId,
            data: true,
          },
          "*"
        );
        props.global.setLoading(false);
        setAllowedMethods(allowedMethods);
        if (Object.values(allowedMethods).some((method) => method)) {
          props.global.setReady(true);
          window.parent.postMessage(
            {
              event:
                "callback-payabli-function-expresscheckout-ready" +
                config.randomId,
              data: allowedMethods,
            },
            "*"
          );
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, setTokenUI, resizeContainer, applePayScript]
  );

  const initialQuery = useCallback(async () => {
    if (!config?.entryPoint || config?.entryPoint?.length === 0) {
      setErrorMessage("Entry point not found");
      return;
    }
    // Config token only for init. It returns the first single-use temporary
    // token, which becomes the one current token (`vTerminal.token`); every
    // call that spends it reads it at call time and stores the rotated one.
    const { data: initData = {} } = await axios.get(
      process.env.REACT_APP_URL_API + `Tools/init/method/${config.entryPoint}`,
      {
        headers: {
          requestToken: config.token,
          ...loaderVersionHeaders(),
        },
      }
    );
    props.vTerminal.setToken(initData.identifier);
    // Skip the availability call when no enabled wallet can be shown anyway.
    const availability =
      initData.applePay === true || initData.googlePay === true
        ? await fetchWalletAvailability({
            entry: config.entryPoint,
            host: domain,
            getToken: () => props.vTerminal.token,
            setToken: (token) => props.vTerminal.setToken(token),
          })
        : NO_WALLET_AVAILABILITY;
    return { initData, availability };
  }, [config, domain, props.vTerminal]);

  useEffect(() => {
    if (domain) {
      // Check if any service is enabled
      const hasServiceEnabled = Object.values(
        config.expressCheckout || {}
      ).some((service) => service.enabled);
      if (hasServiceEnabled) {
        initialQuery()
          .then((response) => {
            // initialQuery resolves undefined (after setting its own error)
            // when the entry point is missing.
            if (!response) return undefined;
            return loadExpressCheckout(response, config, domain);
          })
          .catch((error) => {
            console.error(error);
            setErrorMessage(INIT_ERROR_MESSAGE);
            props.global.setLoading(false);
          });
      }
    }
  }, [config, initialQuery, loadExpressCheckout, domain, props.global]);

  const handleMessage = useCallback(
    (event) => {
      if (
        event.data.event ===
        `callback-payabli-function-set-domain${config.randomId}`
      ) {
        if (event.data.data) {
          setDomain(event.data.data);
        }
      }
    },
    [config]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    window.parent.postMessage(
      {
        event: "callback-payabli-function-init-domain" + config.randomId,
      },
      "*"
    );
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [config, handleMessage]);

  if (errorMessage) {
    return <p className="small text-center">{errorMessage}</p>;
  }

  const atleast2Wallets =
    Object.values(allowedMethods).filter((method) => method).length > 1;
  const columns = atleast2Wallets ? config?.expressCheckout?.columns : 1;
  return (
    <>
      <div
        style={{
          display: "grid",
          position: "relative",
          gap: "1rem",
          padding: "0.25rem",
          maxWidth: "none",
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        }}
        className="container w-100"
      >
        {/* Apple pay button */}
        {allowedMethods.applePay && (
          <ApplePayButton
            paypoint={paypoint}
            domain={domain}
            vTerminal={props.vTerminal}
            config={config}
            merchantIdentifier={applePayMerchantId}
            enabled={allowedMethods.applePay}
          />
        )}
        {/* Google pay button */}
        {allowedMethods.googlePay && googlePayInstance && (
          <GooglePayButton instance={googlePayInstance} config={config} />
        )}
      </div>
    </>
  );
};
export default inject("vTerminal", "global")(observer(ExpressCheckout));
